import { Clock3, Minus, Plus, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { durationLabel, durationMinutes } from "../lib/time";

const hours = Array.from({ length: 12 }, (_, index) => index + 1);
const standardMinutes = Array.from({ length: 12 }, (_, index) => index * 5);
const durationPresets = [30, 45, 60, 90, 120, 180];

function minutesFromTime(value: string) {
  const [hour, minute] = value.slice(0, 5).split(":").map(Number);
  return hour * 60 + minute;
}

function timeFromMinutes(value: number) {
  const safe = Math.max(0, Math.min(23 * 60 + 59, value));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}

function clockLabel(value: string) {
  const [hour, minute] = value.split(":").map(Number);
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" });
}

function shortDuration(value: number) {
  if (value < 60) return `${value}m`;
  const hoursPart = Math.floor(value / 60);
  const minutesPart = value % 60;
  return minutesPart ? `${hoursPart}h${minutesPart}` : `${hoursPart}h`;
}

function WheelColumn({ label, displayLabel, value, options, disabled, onSelect }: { label: string; displayLabel: string; value: string; options: { value: string; label: string }[]; disabled: boolean; onSelect: (value: string) => void }) {
  const scroller = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | undefined>(undefined);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const selected = scroller.current?.querySelector<HTMLElement>(`[data-wheel-value="${value}"]`);
    if (selected && scroller.current) scroller.current.scrollTop = selected.offsetTop - (scroller.current.clientHeight - selected.clientHeight) / 2;
  }, [value]);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const settleSelection = () => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const element = scroller.current;
      if (!element) return;
      const center = element.scrollTop + element.clientHeight / 2;
      const candidates = [...element.querySelectorAll<HTMLButtonElement>("[data-wheel-value]")];
      const closest = candidates.reduce<HTMLButtonElement | null>((nearest, candidate) => {
        if (!nearest) return candidate;
        const candidateDistance = Math.abs(candidate.offsetTop + candidate.offsetHeight / 2 - center);
        const nearestDistance = Math.abs(nearest.offsetTop + nearest.offsetHeight / 2 - center);
        return candidateDistance < nearestDistance ? candidate : nearest;
      }, null);
      const nextValue = closest?.dataset.wheelValue;
      if (nextValue && nextValue !== valueRef.current) onSelect(nextValue);
    }, 90);
  };

  return <div className="time-wheel">
    <span>{displayLabel}</span>
    <div className="time-wheel-window" ref={scroller} onScroll={settleSelection} role="group" aria-label={`${label} roller`}>
      <i aria-hidden="true" />
      {options.map((option) => <button type="button" key={option.value} data-wheel-value={option.value} aria-label={`${label} ${option.label}`} aria-pressed={option.value === value} disabled={disabled} onClick={() => onSelect(option.value)}>{option.label}</button>)}
      <i aria-hidden="true" />
    </div>
  </div>;
}

export function TimeDurationRoller({
  startTime,
  endTime,
  label,
  durationLabelText = "Duration",
  compact = false,
  disabled = false,
  onChange,
}: {
  startTime: string;
  endTime: string;
  label: string;
  durationLabelText?: string;
  compact?: boolean;
  disabled?: boolean;
  onChange: (startTime: string, endTime: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ left: number; top: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLElement>(null);
  const savedStart = startTime || "09:00";
  const savedDuration = Math.min(360, durationMinutes(savedStart, endTime));
  const savedEnd = timeFromMinutes(minutesFromTime(savedStart) + savedDuration);
  const [draftStart, setDraftStart] = useState(savedStart);
  const [draftEnd, setDraftEnd] = useState(savedEnd);
  const normalizedStart = compact && open ? draftStart : savedStart;
  const workingEnd = compact && open ? draftEnd : savedEnd;
  const startMinutes = minutesFromTime(normalizedStart);
  const hour24 = Math.floor(startMinutes / 60);
  const minute = startMinutes % 60;
  const hour12 = hour24 % 12 || 12;
  const period = hour24 >= 12 ? "PM" : "AM";
  const duration = Math.min(360, durationMinutes(normalizedStart, workingEnd));
  const normalizedEnd = timeFromMinutes(startMinutes + duration);
  const minuteOptions = standardMinutes.includes(minute) ? standardMinutes : [...standardMinutes, minute].sort((left, right) => left - right);
  const durationProgress = (duration - 15) / (360 - 15) * 100;

  const closeAndCommit = useCallback(() => {
    if (compact && (draftStart !== savedStart || draftEnd !== savedEnd)) {
      onChange(draftStart, draftEnd);
    }
    setOpen(false);
  }, [compact, draftEnd, draftStart, onChange, savedEnd, savedStart]);

  const toggleEditor = () => {
    if (open) return closeAndCommit();
    setDraftStart(savedStart);
    setDraftEnd(savedEnd);
    setOpen(true);
  };

  const changeWorkingTime = (nextStart: string, nextEnd: string) => {
    if (compact) {
      setDraftStart(nextStart);
      setDraftEnd(nextEnd);
    } else {
      onChange(nextStart, nextEnd);
    }
  };

  useEffect(() => {
    if (!compact || !open) return;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!root.current?.contains(target) && !popover.current?.contains(target)) closeAndCommit();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAndCommit();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [closeAndCommit, compact, open]);

  useEffect(() => {
    if (!compact || !open) return;
    const placePopover = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect || window.matchMedia?.("(max-width: 540px)").matches) return setPopoverPosition(null);
      const width = Math.min(365, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const top = window.innerHeight - rect.bottom >= 355 ? rect.bottom + 7 : Math.max(12, rect.top - 350);
      setPopoverPosition({ left, top });
    };
    placePopover();
    window.addEventListener("resize", placePopover);
    window.addEventListener("scroll", placePopover, true);
    return () => {
      window.removeEventListener("resize", placePopover);
      window.removeEventListener("scroll", placePopover, true);
    };
  }, [compact, open]);

  const updateStart = (nextHour: number, nextMinute: number, nextPeriod: string) => {
    const nextHour24 = (nextHour % 12) + (nextPeriod === "PM" ? 12 : 0);
    const nextStart = timeFromMinutes(nextHour24 * 60 + nextMinute);
    changeWorkingTime(nextStart, timeFromMinutes(minutesFromTime(nextStart) + duration));
  };
  const nudgeStart = (amount: number) => {
    const nextStart = timeFromMinutes(startMinutes + amount);
    changeWorkingTime(nextStart, timeFromMinutes(minutesFromTime(nextStart) + duration));
  };
  const updateDuration = (nextDuration: number) => changeWorkingTime(normalizedStart, timeFromMinutes(startMinutes + nextDuration));

  const editor = <section ref={popover} className={`time-sculptor ${compact ? "popover" : "embedded"}`} role={compact ? "dialog" : "group"} aria-label={`${label} and duration`} style={compact && popoverPosition ? { left: popoverPosition.left, top: popoverPosition.top } : undefined}>
    <header>
      <span><Sparkles size={14} /></span>
      <div><small>TIME SCULPTOR</small><strong>{clockLabel(normalizedStart)} <i>→</i> {clockLabel(normalizedEnd)}</strong></div>
      {compact && <button type="button" aria-label="Close time sculptor" onClick={closeAndCommit}><X size={15} /></button>}
    </header>
    <div className="time-wheel-grid">
      <WheelColumn label={`${label} hour`} displayLabel="Hour" value={String(hour12)} options={hours.map((value) => ({ value: String(value), label: String(value) }))} disabled={disabled} onSelect={(value) => updateStart(Number(value), minute, period)} />
      <WheelColumn label={`${label} minute`} displayLabel="Minute" value={String(minute).padStart(2, "0")} options={minuteOptions.map((value) => ({ value: String(value).padStart(2, "0"), label: String(value).padStart(2, "0") }))} disabled={disabled} onSelect={(value) => updateStart(hour12, Number(value), period)} />
      <WheelColumn label={`${label} period`} displayLabel="AM / PM" value={period} options={[{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }]} disabled={disabled} onSelect={(value) => updateStart(hour12, minute, value)} />
    </div>
    <div className="time-nudges"><button type="button" aria-label={`${label} 15 minutes earlier`} disabled={disabled || startMinutes < 15} onClick={() => nudgeStart(-15)}><Minus size={12} />15 min</button><span>Scroll or tap the rollers</span><button type="button" aria-label={`${label} 15 minutes later`} disabled={disabled || startMinutes > 23 * 60 + 44} onClick={() => nudgeStart(15)}><Plus size={12} />15 min</button></div>
    <div className="duration-sculptor">
      <div><span>Duration</span><strong>{durationLabel(duration)}</strong><small>Ends {clockLabel(normalizedEnd)}</small></div>
      <input type="range" min="15" max="360" step="15" value={duration} aria-label={durationLabelText} disabled={disabled} style={{ background: `linear-gradient(90deg, var(--terra) 0 ${durationProgress}%, #dedad2 ${durationProgress}% 100%)` }} onChange={(event) => updateDuration(Number(event.target.value))} />
      <div className="duration-presets">{durationPresets.map((value) => <button type="button" key={value} aria-label={`Set ${durationLabelText} to ${durationLabel(value)}`} aria-pressed={duration === value} disabled={disabled} onClick={() => updateDuration(value)}>{shortDuration(value)}</button>)}</div>
    </div>
    <p className="time-sculptor-hint"><Clock3 size={12} />{compact ? "Changes apply together when this popup closes." : "Changes stay in this activity draft until you save."}</p>
    {compact && <button type="button" className="time-sculptor-apply" onClick={closeAndCommit}>Apply to itinerary</button>}
  </section>;

  return <div ref={root} className={`time-duration-roller${compact ? " compact" : ""}${open ? " open" : ""}`}>
    {compact && <button ref={trigger} type="button" className="time-capsule-trigger" aria-label={`Adjust ${label} and duration`} aria-haspopup="dialog" aria-expanded={open} onClick={toggleEditor}>
      <span className="time-capsule-copy"><small><Clock3 size={10} />Start</small><strong>{clockLabel(savedStart)}</strong><em>ends {clockLabel(savedEnd)}</em></span>
      <span className="duration-orbit" style={{ background: `conic-gradient(var(--terra) ${Math.max(20, savedDuration)}deg, #e4e0d8 0)` }}><i>{shortDuration(savedDuration)}</i></span>
    </button>}
    {!compact && editor}
    {compact && open && typeof document !== "undefined" && createPortal(editor, document.body)}
  </div>;
}
